import React from 'react';
import { BackgroundType, usePreferences } from '@/contexts/PreferencesContext';
import { Squares } from '@/components/ui/squares-background';
import OptimizedInfiniteHero from '@/components/ui/OptimizedInfiniteHero';
import { VideoBackground } from '@/components/ui/VideoBackground';


interface BackgroundVariantsProps {
  type: BackgroundType;
  isPreview?: boolean;
  pauseAnimations?: boolean;
  qualityLevel?: 'low' | 'medium' | 'high';
}

// Enhanced Squares Components with color variations - Optimized
const SquaresOrange: React.FC<{ pauseAnimations?: boolean }> = ({ pauseAnimations }) => (
  <Squares 
    borderColor="rgba(255, 107, 53, 0.3)" 
    hoverFillColor="rgba(255, 140, 90, 0.2)"
    speed={pauseAnimations ? 0 : 1.5}
  />
);

const SquaresBlue: React.FC<{ pauseAnimations?: boolean }> = ({ pauseAnimations }) => (
  <Squares 
    borderColor="rgba(79, 156, 249, 0.3)" 
    hoverFillColor="rgba(147, 197, 253, 0.2)"
    speed={pauseAnimations ? 0 : 1.2}
  />
);

const SquaresGreen: React.FC<{ pauseAnimations?: boolean }> = ({ pauseAnimations }) => (
  <Squares 
    borderColor="rgba(82, 214, 129, 0.3)" 
    hoverFillColor="rgba(134, 239, 172, 0.2)"
    speed={pauseAnimations ? 0 : 1.8}
  />
);

// Shader-based landscape variants (use OptimizedInfiniteHero for better performance)
const ShaderSunset: React.FC<{ isPreview?: boolean }> = ({ isPreview }) => <OptimizedInfiniteHero isPreview={isPreview} id="shader-sunset" />;
const ShaderOcean: React.FC<{ isPreview?: boolean }> = ({ isPreview }) => <OptimizedInfiniteHero isPreview={isPreview} id="shader-ocean" />;
const ShaderForest: React.FC<{ isPreview?: boolean }> = ({ isPreview }) => <OptimizedInfiniteHero isPreview={isPreview} id="shader-forest" />;
const ShaderCosmic: React.FC<{ isPreview?: boolean }> = ({ isPreview }) => <OptimizedInfiniteHero isPreview={isPreview} id="shader-cosmic" />;
const ShaderAurora: React.FC<{ isPreview?: boolean }> = ({ isPreview }) => <OptimizedInfiniteHero isPreview={isPreview} id="shader-aurora" />;

// Geometric pattern components
const GeometricHexagon: React.FC = () => (
  <div className="absolute inset-0 opacity-30">
    <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id="hexagon" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
          <polygon
            points="30,5 50,17.5 50,37.5 30,50 10,37.5 10,17.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
            className="text-primary/20"
          />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#hexagon)" />
    </svg>
  </div>
);

const GeometricTriangle: React.FC = () => (
  <div className="absolute inset-0 opacity-20">
    <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id="triangle" x="0" y="0" width="40" height="35" patternUnits="userSpaceOnUse">
          <polygon
            points="20,5 35,30 5,30"
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
            className="text-pink-500/30"
          />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#triangle)" />
    </svg>
  </div>
);

const GeometricDiamond: React.FC = () => (
  <div className="absolute inset-0 opacity-25">
    <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id="diamond" x="0" y="0" width="50" height="50" patternUnits="userSpaceOnUse">
          <polygon
            points="25,5 45,25 25,45 5,25"
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
            className="text-amber-500/30"
          />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#diamond)" />
    </svg>
  </div>
);

const GeometricWave: React.FC = () => (
  <div className="absolute inset-0 opacity-30">
    <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id="wave" x="0" y="0" width="100" height="20" patternUnits="userSpaceOnUse">
          <path
            d="M0,10 Q25,0 50,10 T100,10"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="text-cyan-500/40"
          />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#wave)" />
    </svg>
  </div>
);

// Gradient backgrounds
const GradientWarm: React.FC = () => (
  <div 
    className="absolute inset-0"
    style={{
      background: 'linear-gradient(135deg, #f97316 0%, #dc2626 50%, #ea580c 100%)',
      opacity: 0.1
    }}
  />
);

const GradientCool: React.FC = () => (
  <div 
    className="absolute inset-0"
    style={{
      background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #06b6d4 100%)',
      opacity: 0.1
    }}
  />
);

const GradientNeon: React.FC = () => (
  <div 
    className="absolute inset-0"
    style={{
      background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 30%, #f59e0b 60%, #10b981 100%)',
      opacity: 0.15
    }}
  />
);

const GradientEarth: React.FC = () => (
  <div 
    className="absolute inset-0"
    style={{
      background: 'linear-gradient(135deg, #92400e 0%, #059669 50%, #1f2937 100%)',
      opacity: 0.12
    }}
  />
);

// Particle system components (optimized with GPU acceleration)
const ParticleFlow: React.FC = () => (
  <div className="absolute inset-0 contain-paint">
    {Array.from({ length: 15 }).map((_, i) => (
      <div
        key={i}
        className="absolute w-1 h-1 bg-gray-500 rounded-full animate-pulse"
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          animationDelay: `${Math.random() * 2}s`,
          animationDuration: `${2 + Math.random() * 3}s`,
          transform: 'translateZ(0)',
          willChange: 'opacity'
        }}
      />
    ))}
  </div>
);

const ParticleSpark: React.FC = () => (
  <div className="absolute inset-0 contain-paint">
    {Array.from({ length: 12 }).map((_, i) => (
      <div
        key={i}
        className="absolute w-0.5 h-0.5 bg-yellow-400 rounded-full"
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          animation: `particleFloat ${3 + Math.random() * 2}s infinite ease-in-out`,
          animationDelay: `${Math.random() * 3}s`,
          transform: 'translateZ(0)',
          willChange: 'transform, opacity'
        }}
      />
    ))}
  </div>
);

const ParticleGalaxy: React.FC = () => (
  <div className="absolute inset-0 contain-paint">
    {Array.from({ length: 18 }).map((_, i) => (
      <div
        key={i}
        className="absolute w-1 h-1 bg-purple-400 rounded-full opacity-60"
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          animation: `particleFloat ${4 + Math.random() * 4}s infinite ease-in-out`,
          animationDelay: `${Math.random() * 4}s`,
          transform: 'translateZ(0)',
          willChange: 'transform, opacity'
        }}
      />
    ))}
  </div>
);

const ParticleNeural: React.FC = () => (
  <div className="absolute inset-0 contain-paint">
    {Array.from({ length: 10 }).map((_, i) => (
      <div
        key={i}
        className="absolute w-2 h-2 bg-emerald-500 rounded-full opacity-40"
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          animation: `particleFloat ${5 + Math.random() * 3}s infinite ease-in-out`,
          animationDelay: `${Math.random() * 5}s`,
          transform: 'translateZ(0)',
          willChange: 'transform, opacity'
        }}
      />
    ))}
  </div>
);

export const BackgroundVariants: React.FC<BackgroundVariantsProps> = ({ 
  type, 
  isPreview = false, 
  pauseAnimations = false,
  qualityLevel = 'medium'
}) => {
  const { backgroundOverlayOpacity, frostedGlassMode } = usePreferences();
  const renderBackground = () => {
    switch (type) {
      // Current options - optimized
      case 'squares':
        return <Squares speed={pauseAnimations ? 0 : 1} />;
      
      // Animated squares variants - optimized
      case 'squares-orange':
        return <SquaresOrange pauseAnimations={pauseAnimations} />;
      case 'squares-blue':
        return <SquaresBlue pauseAnimations={pauseAnimations} />;
      case 'squares-green':
        return <SquaresGreen pauseAnimations={pauseAnimations} />;
      
      // Shader-based landscapes
      case 'shader-sunset':
        return <ShaderSunset isPreview={isPreview} />;
      case 'shader-ocean':
        return <ShaderOcean isPreview={isPreview} />;
      case 'shader-forest':
        return <ShaderForest isPreview={isPreview} />;
      case 'shader-cosmic':
        return <ShaderCosmic isPreview={isPreview} />;
      case 'shader-aurora':
        return <ShaderAurora isPreview={isPreview} />;
      
      // Geometric patterns
      case 'geometric-hexagon':
        return <GeometricHexagon />;
      case 'geometric-triangle':
        return <GeometricTriangle />;
      case 'geometric-diamond':
        return <GeometricDiamond />;
      case 'geometric-wave':
        return <GeometricWave />;
      
      // Gradient backgrounds
      case 'gradient-warm':
        return <GradientWarm />;
      case 'gradient-cool':
        return <GradientCool />;
      case 'gradient-neon':
        return <GradientNeon />;
      case 'gradient-earth':
        return <GradientEarth />;
      
      // Particle systems
      case 'particle-flow':
        return <ParticleFlow />;
      case 'particle-spark':
        return <ParticleSpark />;
      case 'particle-galaxy':
        return <ParticleGalaxy />;
      case 'particle-neural':
        return <ParticleNeural />;
      
      // Video backgrounds
      case 'video-abstract-particles':
      case 'video-ocean-waves':
      case 'video-forest-mist':
      case 'video-digital-matrix':
      case 'video-cosmic-nebula':
      case 'video-flowing-water':
      case 'video-night-city':
      case 'video-golden-sunset':
      case 'video-aurora-lights':
      case 'video-fire-flames':
        return <VideoBackground type={type} isPreview={isPreview} pauseAnimations={pauseAnimations} />;
      
      default:
        // Fallback to default squares
        return <Squares />;
    }
  };

  return (
    <div className="fixed inset-0 -z-10">
      {renderBackground()}
      {/* Enhanced Overlay System - Regular or Frosted Glass */}
      {!isPreview && backgroundOverlayOpacity > 0 && (
        <div 
          className="absolute inset-0 pointer-events-none transition-all duration-700 ease-out"
          style={{ 
            opacity: backgroundOverlayOpacity / 100,
            zIndex: 10
          }}
        >
          {frostedGlassMode ? (
            // Glass effect - optimized blur levels to reduce GPU strain
            <div 
              className={`absolute inset-0 will-change-blur ${
                backgroundOverlayOpacity === 0 ? '' :
                backgroundOverlayOpacity <= 25 ? 'backdrop-blur-[2px] bg-white/3 border border-white/10' :
                backgroundOverlayOpacity <= 50 ? 'backdrop-blur-[4px] bg-white/5 border border-white/15' :
                backgroundOverlayOpacity <= 75 ? 'backdrop-blur-[6px] bg-white/8 border border-white/20' :
                'backdrop-blur-[8px] bg-white/10 border border-white/25'
              }`}
            />
          ) : (
            // Enhanced Dark Vignette Mode
            <>
              {/* Base Vignette Layer - Enhanced for deeper darkness */}
              <div 
                className="absolute inset-0"
                style={{
                  background: `radial-gradient(circle at center, 
                    transparent 10%, 
                    rgba(0, 0, 0, ${Math.min(backgroundOverlayOpacity * 0.002, 0.2)}) 35%, 
                    rgba(0, 0, 0, ${Math.min(backgroundOverlayOpacity * 0.005, 0.5)}) 65%, 
                    rgba(0, 0, 0, ${Math.min(backgroundOverlayOpacity * 0.01, 0.9)}) 100%)`
                }}
              />
              
              {/* Enhanced Edge Shadow Layer */}
              <div 
                className="absolute inset-0"
                style={{
                  boxShadow: `
                    inset 0 0 ${Math.min(backgroundOverlayOpacity * 2, 200)}px rgba(0, 0, 0, ${Math.min(backgroundOverlayOpacity * 0.006, 0.6)}),
                    inset 0 0 ${Math.min(backgroundOverlayOpacity * 4, 400)}px rgba(0, 0, 0, ${Math.min(backgroundOverlayOpacity * 0.003, 0.3)})
                  `
                }}
              />
              
              {/* Corner Darkening - Enhanced */}
              <div 
                className="absolute inset-0"
                style={{
                  background: `
                    linear-gradient(135deg, rgba(0, 0, 0, ${Math.min(backgroundOverlayOpacity * 0.004, 0.4)}) 0%, transparent 35%),
                    linear-gradient(225deg, rgba(0, 0, 0, ${Math.min(backgroundOverlayOpacity * 0.004, 0.4)}) 0%, transparent 35%),
                    linear-gradient(315deg, rgba(0, 0, 0, ${Math.min(backgroundOverlayOpacity * 0.004, 0.4)}) 0%, transparent 35%),
                    linear-gradient(45deg, rgba(0, 0, 0, ${Math.min(backgroundOverlayOpacity * 0.004, 0.4)}) 0%, transparent 35%)
                  `
                }}
              />
              
              {/* Subtle Blur Enhancement - Removed to reduce GPU strain */}
            </>
          )}
        </div>
      )}
    </div>
  );
};